import { FeatureFlagName, UserInterface } from '@interfaces/User.interface';
import { PermissionType } from '@constants/types';
import { useStore } from '@hooks/useStore';

export type MarketIntelligenceScope = 'mi:read' | 'mi:write';

export interface MarketIntelligenceAuthStore {
  user?: UserInterface | null;
  hasFeature?: (flag: FeatureFlagName) => boolean;
  hasPermission?: (permissions: PermissionType[]) => boolean;
}

export type MarketIntelligenceTarget =
  | MarketIntelligenceAuthStore
  | UserInterface
  | null
  | undefined;

const DEFAULT_MARKETPLACE_INTELLIGENCE_PERMISSIONS: PermissionType[] = [
  'market_intelligence_view',
  'market_intelligence_manage'
];

/** Tenant feature name from the merged CRM API (`Feature::NAME_CAN_VIEW_MARKET_INTELLIGENCE`). */
export const MARKET_INTELLIGENCE_TENANT_FEATURE = 'Can view market intelligence';

/**
 * True when GET /me/feature has the merged Market Intelligence feature on.
 * Also accepts the earlier ticket name so either API shape unlocks the area.
 */
export function tenantHasMarketIntelligenceFeature(
  features: Record<string, boolean> | undefined
): boolean {
  if (!features) return false;
  return (
    features[MARKET_INTELLIGENCE_TENANT_FEATURE] === true ||
    features['Market intelligence'] === true
  );
}

let rolloutOverride: boolean | null | undefined;

/**
 * Temporary rollout gate until FLT-2410 (tenancy/data isolation) lands.
 * Can be disabled via `VITE_MARKETPLACE_INTELLIGENCE_ENABLED=false` or overridden in tests.
 */
export const setMarketIntelligenceRolloutOverride = (
  value: boolean | null | undefined
): void => {
  rolloutOverride = value;
};

export const isMarketIntelligenceRolloutEnabled = (): boolean => {
  if (typeof rolloutOverride === 'boolean') {
    return rolloutOverride;
  }
  const envFlag = import.meta.env.VITE_MARKETPLACE_INTELLIGENCE_ENABLED;
  if (envFlag === 'false' || envFlag === '0') {
    return false;
  }
  return true;
};

const normalizeAuthStore = (
  target: MarketIntelligenceTarget
): MarketIntelligenceAuthStore | null => {
  if (!target) {
    return null;
  }
  if (
    'user' in target ||
    'hasFeature' in target ||
    'hasPermission' in target
  ) {
    return target as MarketIntelligenceAuthStore;
  }
  return { user: target as UserInterface };
};

/**
 * Checks whether the `/me` user payload includes the given Marketplace Intelligence scope (`mi:read` or `mi:write`).
 * Supports `user.scopes`, `user.marketplace_intelligence_scopes`, `user.permissions`, and role permission names.
 */
export const hasMarketplaceIntelligenceScope = (
  user: UserInterface | null | undefined,
  scope: MarketIntelligenceScope
): boolean => {
  if (!user) return false;

  if (Array.isArray(user.scopes) && user.scopes.includes(scope)) {
    return true;
  }

  if (
    Array.isArray(user.marketplace_intelligence_scopes) &&
    user.marketplace_intelligence_scopes.includes(scope)
  ) {
    return true;
  }

  if (Array.isArray(user.permissions) && user.permissions.includes(scope)) {
    return true;
  }

  if (Array.isArray(user.roles)) {
    for (const role of user.roles) {
      if (
        Array.isArray(role.permissions) &&
        role.permissions.some((p) => p?.name === scope)
      ) {
        return true;
      }
    }
  }

  return false;
};

const hasMarketplaceIntelligenceFeatureFlag = (
  authStore: MarketIntelligenceAuthStore
): boolean => {
  if (authStore.hasFeature?.('market_intelligence')) {
    return true;
  }
  return Boolean(
    authStore.user?.canAccessMarketIntelligence ||
      authStore.user?.feature_flags?.market_intelligence
  );
};

const hasMarketplaceIntelligencePermission = (
  authStore: MarketIntelligenceAuthStore,
  permissions: PermissionType[] = DEFAULT_MARKETPLACE_INTELLIGENCE_PERMISSIONS
): boolean => {
  if (authStore.hasPermission) {
    return authStore.hasPermission(permissions);
  }
  const userPermissions = authStore.user?.permissions;
  if (!Array.isArray(userPermissions) || userPermissions.length === 0) {
    return false;
  }
  return permissions.some((p) => userPermissions.includes(p));
};

/**
 * Determines whether the user can view the Market Intelligence area (`FLT-2412` + `FLT-2811`).
 *
 * Requires ALL of:
 * 1. Temporary rollout flag (`VITE_MARKETPLACE_INTELLIGENCE_ENABLED` until `FLT-2410` lands)
 * 2. Tenant feature from `GET /me/feature` (`Can view market intelligence`)
 * 3. Role permission from `GET /me` (`Market Intelligence View` or `Market Intelligence Manage`).
 *    `mi:read` / `mi:write` on the user are a fixture fallback only.
 */
export const canAccessMarketIntelligence = (
  target?: MarketIntelligenceTarget,
  permissions: PermissionType[] = DEFAULT_MARKETPLACE_INTELLIGENCE_PERMISSIONS
): boolean => {
  const authStore = normalizeAuthStore(target);
  if (!authStore?.user) {
    return false;
  }

  if (!isMarketIntelligenceRolloutEnabled()) {
    return false;
  }

  if (!hasMarketplaceIntelligenceFeatureFlag(authStore)) {
    return false;
  }

  return (
    hasMarketplaceIntelligencePermission(authStore, permissions) ||
    hasMarketplaceIntelligenceScope(authStore.user, 'mi:read') ||
    hasMarketplaceIntelligenceScope(authStore.user, 'mi:write')
  );
};

/**
 * Write access matches `InsightsScope::resolveFromPermissions`: a SEG tenant
 * (`tenant_is_seg_parent`) with view access, or a manage permission / `mi:write`
 * fixture. The CRM has no `market_intelligence_manage` permission.
 */
export const canWriteMarketIntelligence = (
  target?: MarketIntelligenceTarget,
  permissions: PermissionType[] = DEFAULT_MARKETPLACE_INTELLIGENCE_PERMISSIONS
): boolean => {
  const authStore = normalizeAuthStore(target);
  if (!authStore || !canAccessMarketIntelligence(authStore, permissions)) {
    return false;
  }
  return (
    Boolean(authStore.user?.company?.tenant_is_seg_parent) ||
    hasMarketplaceIntelligencePermission(authStore, [
      'market_intelligence_manage'
    ]) ||
    hasMarketplaceIntelligenceScope(authStore.user, 'mi:write')
  );
};

/**
 * Hook for Market Intelligence read/write gating in components and sidebar navigation.
 */
export const useMarketIntelligenceAccess = () => {
  const { authStore } = useStore();
  return {
    canRead: canAccessMarketIntelligence(authStore),
    canWrite: canWriteMarketIntelligence(authStore)
  };
};
